import React, {useState} from 'react';

import {useMutation} from '@apollo/client/react';
import {X} from 'lucide-react';
import {RadioCardSelect} from '../RadioCardSelect';
import {gql} from 'types/__generated__';

const SET_PERMISSION_GROUP = gql(`
  mutation SetPermissionGroupForUsers($usernames: [String!]!, $organisationId: UUID, $permissionGroupId: UUID!) {
    setPermissionGroupForUsers(usernames: $usernames, organisationId: $organisationId, permissionGroupId: $permissionGroupId)
  }
`);

interface RoleOption {
  id: string;
  name: string;
}

interface ChangePermissionGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  username: string;
  userDisplayName: string;
  userEmail: string;
  organisationId: string;
  /**
   * The member's current role. It is not guaranteed to be one of `roles`: a retired role (the
   * old "Organisation owner", shown as Admin) or a role above the caller comes back with
   * grantableByMe = false. It is then shown as the selected, disabled option - never hidden,
   * never replaced by the first option.
   */
  currentRole: RoleOption | null;
  /** The roles the caller may hand out - Organisation.roles filtered on grantableByMe. */
  roles: RoleOption[];
}

export function ChangePermissionGroupDialog({
  isOpen,
  onClose,
  onSuccess,
  username,
  userDisplayName,
  userEmail,
  organisationId,
  currentRole,
  roles,
}: ChangePermissionGroupDialogProps) {
  const currentRoleId = currentRole?.id ?? null;
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(currentRoleId);
  const [setPermissionGroup, {loading}] = useMutation(SET_PERMISSION_GROUP);

  if (!isOpen) return null;

  const currentRoleIsOffered = !currentRole || roles.some((role) => role.id === currentRole.id);
  const options = currentRoleIsOffered
    ? roles
    : [{...currentRole!, disabled: true, hint: 'Current role - it cannot be assigned from here'}, ...roles];

  const handleSave = async () => {
    if (!selectedGroupId) return;
    try {
      await setPermissionGroup({
        variables: {
          usernames: [username],
          organisationId,
          permissionGroupId: selectedGroupId,
        },
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to set role:', err);
    }
  };

  const hasChanged = selectedGroupId !== currentRoleId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Change Role</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{userDisplayName} · {userEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X size={18} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <RadioCardSelect options={options} value={selectedGroupId} onChange={setSelectedGroupId} />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedGroupId || !hasChanged || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
